import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { saveAs } from 'file-saver';
import { from } from 'rxjs';
import { ProposalRequest, User } from 'src/app/classes/models';
import { GridActionColumnActions } from 'src/app/interfaces/grid-action-column-actions';
import { GridActionColumnMap } from 'src/app/interfaces/grid-action-column-map';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { AuthService } from 'src/app/services/auth.service';
import { ProposalRequestService } from 'src/app/services/proposal-request.service';
import { ReportService } from 'src/app/services/report.service';
import { ToastService } from 'src/app/services/toast.service';

@Component({
  selector: 'app-proposals',
  templateUrl: './proposals.component.html',
  styleUrls: ['./proposals.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ProposalsComponent implements OnInit {
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private ToastService: ToastService,
    private AuthService: AuthService,
    private ProposalRequestService: ProposalRequestService,
    private ModalService: NgbModal,
    private ReportService: ReportService
  ) {}

  VendorId!: number;
  CompanyId!: number;
  ProposalRequest?: ProposalRequest;
  LoggedInUser!: User;
  reloadGrid: boolean = false;
  title: string = '';

  // Display these actions in the action column.
  ColumnIdToColumnActions: GridActionColumnMap = {
    ['$actionColumn']: ['plane']
  };

  TooltipMap!: {
    [
      key in Extract<
        GridActionColumnActions,
        'view' | 'plane' | 'award' | 'invite'
      >
    ]: string;
  };

  suppliedGridQueryData?: { [key: string]: any };

  goToProposalRequestsGrid() {
    this.router.navigate(['client', this.CompanyId, 'proposal-requests']);
  }

  action({
    action,
    rowData
  }: {
    action: GridActionColumnActions;
    rowData: any;
  }) {
    const {
      id: ProposalRequestVendorId,
      ProposalRequestId,
      ['Proposal.id']: ProposalId,
      ['ProposalRequest.CompanyId']: CompanyId
    }: {
      id: number;
      ProposalRequestId: number;
      ['Proposal.id']: number;
      ['ProposalRequest.CompanyId']: number;
    } = rowData;

    const navigationArray = [];

    const { UserAccountType: { name } = {} } = this.LoggedInUser;

    if (name === 'Vendor') {
      navigationArray.push(
        'vendor',
        this.VendorId,
        'proposal-request',
        ProposalRequestId,
        'proposal-request-vendor',
        ProposalRequestVendorId,
        'proposal'
      );
    } else if (name === 'Client') {
      navigationArray.push(
        'client',
        this.CompanyId,
        'proposal-request',
        ProposalRequestId,
        'proposal-request-vendor',
        ProposalRequestVendorId,
        'proposal'
      );
    } else {
      throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
        returnError: true
      });
    }

    if (ProposalId) {
      navigationArray.push(ProposalId);
    }

    if (['plane', 'view'].includes(action)) {
      this.router.navigate(navigationArray);
    }

    if (action === 'award' && name === 'Client') {
      const ConfirmModal = this.ModalService.open(ConfirmModalComponent, {
        backdrop: 'static',
        modalDialogClass: 'confirm-modal'
      });
      const {
        componentInstance
      }: { componentInstance: ConfirmModalComponent } = ConfirmModal;
      Object.assign(componentInstance, {
        displayText: 'Are you sure you want to award this Vendor?'
      });
      from(ConfirmModal.result).subscribe({
        next: (response) => {
          if (response) {
            this.ProposalRequestService.saveProposalRequestVendor({
              id: ProposalRequestVendorId,
              ProposalRequestId,
              isAwarded: true,
              status: 'AWARDED'
            }).subscribe({
              next: () => {
                this.ProposalRequestService.saveProposalRequest({
                  id: ProposalRequestId,
                  status: 'AWARDED',
                  CompanyId
                }).subscribe({
                  next: () => {
                    this.ToastService.addSuccessToast(
                      'Success',
                      'Successfully awarded the vendor.'
                    );
                    this.reloadGrid = true;
                  }
                });
              }
            });
          }
        }
      });
    }

    if (action === 'invite' && name === 'Client') {
      this.ProposalRequestService.sendProposalRequestVendorEmail({
        id: ProposalRequestVendorId,
        ProposalRequestId
      }).subscribe({
        next: () => {
          this.ToastService.addSuccessToast(
            'Success',
            'Successfully sent bid invitation.'
          );
        }
      });
    }
  }

  getDisplayedColumnActions: ({
    rowData
  }: {
    rowData: any;
  }) => GridActionColumnActions[] = ({ rowData }) => {
    const { UserAccountType: { name } = {} } = this.LoggedInUser;

    const { acceptsRequest } = rowData;

    if (new Set([0, false]).has(acceptsRequest)) {
      return [];
    }

    if (name === 'Client') {
      const {
        ['ProposalRequest.status']: proposalRequestStatus,
        ['Proposal.id']: ProposalId
      } = rowData;
      // Only allow awarding if a Proposal has been submitted.
      if (proposalRequestStatus === 'CLOSED') {
        const actions: GridActionColumnActions[] = [];
        if (ProposalId) {
          actions.push('award');
        } else {
          actions.push('plane');
        }
        actions.push('view');
        return actions;
      }

      if (proposalRequestStatus === 'AWARDED') {
        return ['view'];
      }

      if (proposalRequestStatus === 'RELEASED') {
        const actions: GridActionColumnActions[] = [];
        if (ProposalId) {
          actions.push('view', 'award');
        } else {
          actions.push('plane', 'invite');
        }
        return actions;
      }
    }

    return ['plane'];
  };

  isProposalRequestNotInTerminalStatus(ProposalRequest: ProposalRequest) {
    const { status } = ProposalRequest;

    if (!status) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Bid request is missing status.',
        { returnError: true }
      );
    }

    return !new Set(['CANCELLED', 'AWARDED', 'CLOSED']).has(status);
  }

  areReportsVisible(ProposalRequest: ProposalRequest) {
    const { status } = ProposalRequest;

    if (!status) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Bid request is missing status.',
        { returnError: true }
      );
    }

    return !new Set(['CANCELLED', 'CREATED']).has(status);
  }

  closeProposalRequest() {
    if (!this.ProposalRequest) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing bid request information.',
        { returnError: true }
      );
    }

    const ClonedProposalRequest = structuredClone(this.ProposalRequest);
    this.ProposalRequestService.saveProposalRequest(
      Object.assign(ClonedProposalRequest, { status: 'CLOSED' })
    ).subscribe({
      next: () => {
        Object.assign(this.ProposalRequest!, ClonedProposalRequest);
        this.reloadGrid = true;
        this.ToastService.addSuccessToast(
          'Success',
          'Successfully closed bidding.'
        );
      }
    });
  }

  generateComprehensiveBidReport() {
    if (!this.ProposalRequest) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing bid request information.',
        { returnError: true }
      );
    }

    this.ReportService.generateComprehensiveBidReport({
      ProposalRequestId: this.ProposalRequest.id
    }).subscribe({
      next: (ReportData) => {
        saveAs(new Blob([ReportData]), 'ComprehensiveBidReport.pdf');
      }
    });
  }

  generateBidComparisonReport() {
    if (!this.ProposalRequest) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing bid request information.',
        { returnError: true }
      );
    }

    this.ReportService.generateBidComparisonReport({
      ProposalRequestId: this.ProposalRequest.id
    }).subscribe({
      next: (ReportData) => {
        saveAs(new Blob([ReportData]), 'BidComparisonReport.pdf');
      }
    });
  }

  ngOnInit(): void {
    const paramMap: ParamMap = this.route.snapshot.paramMap;
    const VendorId = Number(paramMap.get('VendorId'));
    const CompanyId = Number(paramMap.get('CompanyId'));
    const ProposalRequestId = Number(paramMap.get('ProposalRequestId'));

    if (VendorId) {
      this.VendorId = VendorId;
    }

    if (CompanyId) {
      this.CompanyId = CompanyId;
    }

    this.LoggedInUser = this.AuthService.getUser();

    this.suppliedGridQueryData = {
      attributes: ['acceptsRequest'],
      // acceptsRequest: {
      //   $or: [true, null]
      // },
      ProposalRequest: {
        attributes: ['id', 'CompanyId', 'status'],
        status: { $notIn: ['CREATED', 'CANCELLED'] },
        Company: {
          attributes: ['id'],
          options: { required: true }
        }
      }
      // Proposal request Vendors SHOULD have an address. Though data discrepancies
      // may prevent a proposal request vendor from having an address.
      // Address: {
      //   options: { required: true }
      // }
    };

    const { UserAccountType: { name } = {} } = this.LoggedInUser;

    this.TooltipMap = {
      view: 'View Bid',
      plane:
        name !== 'Client'
          ? 'Submit bid'
          : "Provide bid information on behalf of vendor or view vendor's bid submission",
      award: 'Award proposal to vendor',
      invite: 'Resend bid email'
    };

    if (name === 'Client') {
      if (!ProposalRequestId) {
        throw this.ToastService.addErrorToast('Error', 'Missing Proposal Id.', {
          returnError: true
        });
      }
      Object.assign(this.suppliedGridQueryData, { ProposalRequestId });

      this.title = 'QuickSourceBids - Review/Report/Finalize Bid';
    } else {
      this.title = 'Bids';
    }

    if (ProposalRequestId) {
      this.ProposalRequestService.getProposalRequest({
        id: ProposalRequestId
      }).subscribe({
        next: (ProposalRequest) => {
          this.ProposalRequest = ProposalRequest;
        }
      });
    }
  }
}
